import * as React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  onSend: (text: string) => void;
  isLoading: boolean;
  isLimitReached: boolean;
  onStartNewChat?: () => void;
};

const AiChatInput: React.FC<Props> = ({
  onSend,
  isLoading,
  isLimitReached,
  onStartNewChat,
}) => {
  const { t } = useTranslation();
  const [text, setText] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  React.useLayoutEffect(() => {
    adjustHeight();
  }, [text]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading || isLimitReached) return;
    onSend(trimmed);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isDisabled = isLoading || isLimitReached;
  const canSend = text.trim().length > 0 && !isDisabled;

  return (
    <div>
      {isLimitReached && (
        <div className="ai-chat-limit-warning">
          <span className="ai-chat-limit-warning-text">
            {t('aiChat.input.limitWarning')}
          </span>
          {onStartNewChat && (
            <button
              type="button"
              className="ai-chat-limit-warning-action"
              onClick={onStartNewChat}>
              {t('aiChat.input.startNewChat')}
            </button>
          )}
        </div>
      )}
      <div
        className={`ai-chat-input-wrapper${isDisabled ? ' disabled' : ''}`}
        onClick={() => !isDisabled && textareaRef.current?.focus()}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('aiChat.input.placeholder')}
          disabled={isDisabled}
          rows={1}
          className="ai-chat-input-textarea"
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`ai-chat-send-btn ${canSend ? 'active' : 'inactive'}`}>
          ↑
        </button>
      </div>
    </div>
  );
};

export default AiChatInput;
